import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import FolderIcon from '~/assets/svg/newSvg/folder.svg?react';
import FolderPlusIcon from '~/assets/svg/newSvg/folder-plus.svg?react';
import styles from './PhotoStorage.module.scss';

interface IEntityPhotos {
  entity_id: number;
  entity_name: string;
  photos_count: number;
}

const PhotoStorageEntities = () => {
  const { t } = useTranslation();
  const { module, assetId } = useParams<{ module: string; assetId: string }>();
  const navigate = useNavigate();
  const toastError = useToastError();

  const moduleName = t(`photo_storage_${module}`);

  const { data: entities, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${assetId}/entities`
      );
      return data as IEntityPhotos[];
    },
    queryKey: ['photo-storage-entities', module, assetId],
    onError: error => toastError(error),
  });

  return (
    <Page heading={<Heading text={`${t('photo_storage')} — ${moduleName}`} />}>
      <div className={styles.container}>
        <div className={styles.grid}>
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className={styles.cardShimmer} style={{ animationDelay: `${i * 0.05}s` }} />
              ))
            : entities?.map(entity => (
              <button
                key={entity.entity_id}
                type="button"
                className={styles.card}
                onClick={() =>
                  navigate(
                    `/documents/photo-storage/${module}/${assetId}/entities/${entity.entity_id}`
                  )
                }
              >
                <FolderIcon className={styles.icon} />
                <p className={styles.name}>{entity.entity_name}</p>
                <span className={styles.count}>
                  {t('photos_count', { count: entity.photos_count })}
                </span>
              </button>
            ))}
          {!entities?.length && !isLoading && (
            <div className={styles.entitiesEmptyState}>
              <div className={styles.entitiesEmptyIcon}>
                <FolderPlusIcon className={styles.entitiesEmptyFolderIcon} />
              </div>
              <p className={styles.entitiesEmptyTitle}>{t('no_entities_yet')}</p>
              <p className={styles.entitiesEmptySubtitle}>
                {t('no_entities_subtitle', { module: moduleName })}
              </p>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export default PhotoStorageEntities;
